import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import LegalEntityDetails from "../components/LegalEntityDetails";
import { useSelector } from "react-redux";
import { FaFileContract, FaCheckCircle, FaShieldAlt } from "react-icons/fa";

const Terms = () => {
  const hotel = useSelector((state) => state.hotel.data);
  const hotelName = hotel?.name || "Ganesh Holiday Homes";
  const email = hotel?.email || "ganeshholidayhomes@gmail.com";
  const contact = hotel?.contact || {
    address: "Narayan Pakhadi Rd, Narayan Pakhadi, Shrivardhan-402110, Maharashtra, India",
    phone: "+91 9673150591",
  };

  return (
    <div className="d-flex flex-column min-vh-100 bg-light">
      <Navbar />

      {/* Hero Banner */}
      <div
        style={{
          background: "linear-gradient(135deg, #07101e 0%, #0b192c 50%, #1e3e62 100%)",
          color: "#ffffff",
          padding: "50px 0 60px 0",
          borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
        }}
      >
        <div className="container text-center">
          <div className="d-inline-flex align-items-center gap-2 bg-white bg-opacity-10 text-white px-3 py-1 rounded-pill small fw-semibold mb-3 border border-white border-opacity-15">
            <span>📜 Guest Agreement & Stay Policies</span>
          </div>
          <h1 className="h1 fw-bold mb-2 font-heading text-white" style={{ fontSize: "2.5rem" }}>
            Terms & Conditions
          </h1>
          <p className="text-white-50 mb-0 mx-auto" style={{ maxWidth: "700px", fontSize: "1.05rem" }}>
            Booking rules, check-in guidelines, and guest responsibilities for {hotelName}.
          </p>
        </div>
      </div>

      {/* Main content */}
      <main className="container flex-grow-1 py-5">
        <div
          className="mx-auto p-4 p-md-5 rounded-4 shadow-sm bg-white border"
          style={{ maxWidth: "900px" }}
        >
          <div className="d-flex align-items-center gap-2 mb-4 text-danger">
            <FaFileContract className="fs-3" />
            <h2 className="h4 fw-bold mb-0 text-dark font-heading">Guest Agreement & Accommodation Terms</h2>
          </div>

          <p className="text-secondary mb-4" style={{ lineHeight: "1.8", fontSize: "1.02rem" }}>
            Welcome to <strong>{hotelName}</strong>. By confirming a room booking through our website, mobile interface, or front desk communication, you agree to the following terms and homestay guidelines:
          </p>

          <hr className="my-4" />

          {/* 1. Nature of Business & Direct Booking */}
          <section className="mb-4">
            <h3 className="h5 fw-bold mb-3 text-dark font-heading">1. Nature of Business & Direct Stay</h3>
            <p className="text-secondary" style={{ lineHeight: "1.7" }}>
              <strong>{hotelName}</strong> operates exclusively as a standalone short-term accommodation property (Homestay / Lodging) in Shrivardhan, Maharashtra. We provide direct room reservations to guests and do not operate as an intermediary travel agency or tour package reseller.
            </p>
          </section>

          <hr className="my-4" />

          {/* 2. Room Reservations & Advance Token */}
          <section className="mb-4">
            <h3 className="h5 fw-bold mb-3 text-dark font-heading">2. Room Reservations & Pricing</h3>
            <ul className="list-unstyled ms-2 text-secondary">
              <li className="mb-2 d-flex align-items-start gap-2">
                <FaCheckCircle className="text-success mt-1 flex-shrink-0" />
                <span><strong>Currency & Tariffs:</strong> All room prices and tariffs are quoted in <strong>Indian Rupees (₹ INR)</strong>.</span>
              </li>
              <li className="mb-2 d-flex align-items-start gap-2">
                <FaCheckCircle className="text-success mt-1 flex-shrink-0" />
                <span><strong>Advance Token Deposit:</strong> A nominal token advance (typically ₹1,000 per room) is required to secure and hold your booking.</span>
              </li>
              <li className="mb-2 d-flex align-items-start gap-2">
                <FaCheckCircle className="text-success mt-1 flex-shrink-0" />
                <span><strong>Balance Payment:</strong> The remaining balance of total room charges is payable at the front desk upon check-in via UPI, cash, or digital payment methods.</span>
              </li>
              <li className="mb-2 d-flex align-items-start gap-2">
                <FaCheckCircle className="text-success mt-1 flex-shrink-0" />
                <span><strong>Confirmation Voucher:</strong> Upon receipt of advance token, an electronic booking acknowledgment is communicated via WhatsApp and Email.</span>
              </li>
            </ul>
          </section>

          <hr className="my-4" />

          {/* 3. Check-in & Check-out Policies */}
          <section className="mb-4">
            <h3 className="h5 fw-bold mb-3 text-dark font-heading">3. Check-In & Check-Out Timings</h3>
            <ul className="list-unstyled ms-2 text-secondary">
              <li className="mb-2"><strong>Standard Check-In:</strong> 12:00 PM (Noon) onwards.</li>
              <li className="mb-2"><strong>Standard Check-Out:</strong> 11:00 AM.</li>
              <li className="mb-2"><strong>Early Check-in / Late Check-out:</strong> Subject to room availability and prior front desk confirmation.</li>
              <li className="mb-2"><strong>Government Photo ID:</strong> In accordance with local regulations, all adult guests (18+ years) must present a valid government-issued photo ID (Aadhaar Card, Driving License, Voter ID, or Passport) at check-in.</li>
            </ul>
          </section>

          <hr className="my-4" />

          {/* 4. Cancellation & Refund Policy */}
          <section className="mb-4">
            <h3 className="h5 fw-bold mb-3 text-dark font-heading">4. Cancellation & Refund Policy</h3>
            <p className="text-secondary mb-2">
              All booking cancellations, date modifications, and refunds are governed strictly by our official policy:
            </p>
            <ul className="list-unstyled ms-2 text-secondary mb-3">
              <li className="mb-1">• 100% full refund if cancelled 7 days or more prior to the scheduled check-in date.</li>
              <li className="mb-1">• 50% refund or free date change if cancelled 3 to 6 days prior to check-in.</li>
              <li className="mb-1">• Non-refundable for cancellations made within 48 hours or no-shows.</li>
            </ul>
            <p className="text-secondary small mb-0">
              For complete terms and refund timelines (5 to 7 bank business days), please view our dedicated{" "}
              <Link to="/cancellation-refund-policy" className="text-danger fw-semibold">
                Cancellation & Refund Policy page →
              </Link>
            </p>
          </section>

          <hr className="my-4" />

          {/* 5. Guest Conduct & Property Care */}
          <section className="mb-4">
            <h3 className="h5 fw-bold mb-3 text-dark font-heading">5. Guest Conduct & House Rules</h3>
            <ul className="list-unstyled ms-2 text-secondary">
              <li className="mb-2 d-flex align-items-start gap-2">
                <FaShieldAlt className="text-danger mt-1 flex-shrink-0" />
                <span><strong>Family Environment:</strong> Ganesh Holiday Homes is a peaceful family stay. Loud music or disruptive behavior after 10:00 PM is strictly prohibited.</span>
              </li>
              <li className="mb-2 d-flex align-items-start gap-2">
                <FaShieldAlt className="text-danger mt-1 flex-shrink-0" />
                <span><strong>No Smoking Indoors:</strong> Smoking is strictly prohibited inside all air-conditioned rooms. Designated outdoor balconies and garden areas are available.</span>
              </li>
              <li className="mb-2 d-flex align-items-start gap-2">
                <FaShieldAlt className="text-danger mt-1 flex-shrink-0" />
                <span><strong>Property Care & Damages:</strong> Guests are expected to treat the homestay premises with care. Any intentional damage or loss of property items will be charged to the guest.</span>
              </li>
              <li className="mb-2 d-flex align-items-start gap-2">
                <FaShieldAlt className="text-danger mt-1 flex-shrink-0" />
                <span><strong>Parking:</strong> Gated on-premise car parking is provided free of cost for staying guests at owner's risk.</span>
              </li>
            </ul>
          </section>

          <hr className="my-4" />

          {/* 6. Contact Support */}
          <section className="text-center pt-2">
            <h3 className="h5 fw-bold mb-2 text-dark font-heading">Questions & Inquiries</h3>
            <p className="text-secondary small mb-3">
              If you have any questions regarding our terms and house policies, contact our front desk:
            </p>
            <div className="d-flex flex-column flex-sm-row gap-2 justify-content-center">
              <a
                href={`mailto:${email}`}
                className="btn btn-outline-danger btn-sm rounded-pill px-4 py-2 fw-semibold"
              >
                📧 {email}
              </a>
              <Link
                to="/contact-us"
                className="btn btn-outline-dark btn-sm rounded-pill px-4 py-2 fw-semibold"
              >
                📍 View Contact & Address
              </Link>
            </div>
            <p className="text-muted small mt-3 mb-0">
              <strong>{hotelName}</strong> • {contact.address} • Tel: {contact.phone}
            </p>
          </section>
        </div>
        <LegalEntityDetails />
      </main>

      <Footer />
    </div>
  );
};

export default Terms;
